/**
 * <spectrum-collection-plp>
 * Product grid with infinite scroll on all viewports (no numbered pagination).
 *
 *  - Desktop: shows the full current Liquid `paginate` page, then loads the next
 *    page on scroll (IntersectionObserver on the sentinel).
 *  - Mobile: reveals `data-mobile-initial` cards, then `data-mobile-batch` more at a
 *    time on scroll; when the in-DOM list is exhausted, fetches the next page too.
 *  - Both: when the current page is exhausted, fetch the next Liquid page via
 *    `?page=N&sections=<section-id>` and append its items. A loader shows during each
 *    network fetch; the sentinel is removed once there are no more products.
 *
 * No dependencies. Self-registers on import.
 */
class SpectrumCollectionPlp extends HTMLElement {
  constructor() {
    super();
    this.io = null;
    this.shown = 0;
    this.items = [];
    this.fetching = false;
    this.mode = null; // 'mobile' | 'desktop'
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.sentinel = this.querySelector('[data-sentinel]');
    this.loader = this.querySelector('[data-loader]');
    if (!this.grid) return;
    this.items = Array.from(this.grid.children);
    this.mobileInitial = parseInt(this.dataset.mobileInitial, 10) || 8;
    this.mobileBatch = parseInt(this.dataset.mobileBatch, 10) || 8;
    this.sectionId = this.dataset.sectionId;
    this.nextUrl = (this.dataset.nextUrl || '').trim();

    this.mq = window.matchMedia('(max-width: 1023px)');
    this._onMqChange = () => this.apply();
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this._onMqChange);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this._onMqChange);
    }
    this.apply();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.mq) {
      if (typeof this.mq.removeEventListener === 'function') {
        this.mq.removeEventListener('change', this._onMqChange);
      } else if (typeof this.mq.removeListener === 'function') {
        this.mq.removeListener(this._onMqChange);
      }
    }
  }

  apply() {
    const wantMobile = this.mq && this.mq.matches;
    const nextMode = wantMobile ? 'mobile' : 'desktop';
    if (nextMode === this.mode) return;
    this.mode = nextMode;
    if (wantMobile) this.enterMobile();
    else this.enterDesktop();
  }

  // Mobile: reveal a limited set first, then batch-reveal on scroll.
  enterMobile() {
    this.shown = Math.min(this.mobileInitial, this.items.length);
    for (let i = 0; i < this.items.length; i++) {
      this.items[i].classList.toggle('scplp__item--hidden', i >= this.shown);
    }
    this.bindObserver();
  }

  // Desktop: show everything already rendered, then infinite-scroll fetch on scroll.
  enterDesktop() {
    for (let i = 0; i < this.items.length; i++) {
      this.items[i].classList.remove('scplp__item--hidden');
    }
    this.shown = this.items.length;
    this.bindObserver();
  }

  bindObserver() {
    if (this.io) this.io.disconnect();
    if (!this.sentinel || !('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.revealNext();
          break;
        }
      }
    }, { rootMargin: '400px 0px' });
    this.io.observe(this.sentinel);
  }

  async revealNext() {
    if (this.fetching) return;
    // Mobile batch-reveal of already-rendered items.
    if (this.shown < this.items.length) {
      const next = Math.min(this.items.length, this.shown + this.mobileBatch);
      for (let i = this.shown; i < next; i++) {
        this.items[i].classList.remove('scplp__item--hidden');
      }
      this.shown = next;
    }
    // In-DOM list exhausted → fetch the next Liquid page.
    if (this.shown >= this.items.length) {
      const appended = await this.fetchNextPage();
      if (!appended) this.tearDownObserver();
    }
  }

  async fetchNextPage() {
    if (!this.nextUrl || !this.sectionId) return false;
    this.fetching = true;
    this._setLoading(true);
    try {
      // Shopify section rendering: ?sections=<sectionId> returns { [sectionId]: '<html>' }
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('sections', this.sectionId);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) return false;
      const json = await res.json();
      const html = json[this.sectionId];
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newItems = doc.querySelectorAll('spectrum-collection-plp [data-grid] > li');
      if (!newItems.length) return false;

      const startIndex = this.items.length;
      const frag = document.createDocumentFragment();
      newItems.forEach((li, idx) => {
        li.dataset.index = String(startIndex + idx);
        li.classList.remove('scplp__item--hidden');
        frag.appendChild(li);
      });
      this.grid.appendChild(frag);
      this.items = Array.from(this.grid.children);
      this.shown = this.items.length;

      // Update next URL from the freshly fetched page's host element.
      const nextHost = doc.querySelector('spectrum-collection-plp');
      this.nextUrl = nextHost && nextHost.dataset ? (nextHost.dataset.nextUrl || '').trim() : '';
      return true;
    } catch (e) {
      return false;
    } finally {
      this.fetching = false;
      this._setLoading(false);
    }
  }

  _setLoading(on) {
    if (this.loader) this.loader.hidden = !on;
    this.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  tearDownObserver() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
      this.sentinel = null;
    }
  }

  // ---- In-place category filtering (no page navigation) ----
  // A category chip normally links to /collections/<handle>/<tag>, a full page load.
  // Instead we fetch just this grid section for that URL via the Section Rendering
  // API and swap it in, so the page (header, chips, scroll feel) stays put — the
  // brief that motivated it: filtering shouldn't feel like landing on a new page.
  async filterTo(url, push = true) {
    if (this.fetching) return;
    this.fetching = true;
    this._setLoading(true);
    // Immediate loading feedback on click: dim + shimmer the current grid so the tap
    // registers visually right away (the bottom loader alone is rarely on-screen). The
    // class rides on the OLD grid and is discarded when innerHTML swaps in the fresh one.
    var _updGrid = this.grid || this.querySelector('[data-grid]');
    if (_updGrid) {
      _updGrid.classList.add('scplp--updating');
      if (!_updGrid.querySelector(':scope > .scplp-skel')) {
        var _sk = document.createElement('div');
        _sk.className = 'scplp-skel';
        _sk.setAttribute('aria-hidden', 'true');
        _updGrid.appendChild(_sk);
      }
    }
    try {
      let freshHost = null;
      // Fast path: render just this grid section for the target URL (works for tag
      // filters and same-template sub-collections — Shopify renders the requested
      // section id against whatever collection the URL resolves to).
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('sections', this.sectionId);
        const res = await fetch(u.toString(), { credentials: 'same-origin' });
        if (res.ok) {
          const json = await res.json();
          const html = json[this.sectionId];
          if (html) {
            freshHost = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('spectrum-collection-plp');
          }
        }
      } catch (inner) {}
      // Fallback: a cross-template target (e.g. a sub-collection on a different
      // template) won't have this section id → fetch its full page and lift the grid.
      if (!freshHost) {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('fetch ' + res.status);
        freshHost = new DOMParser()
          .parseFromString(await res.text(), 'text/html')
          .querySelector('spectrum-collection-plp');
      }
      if (!freshHost) throw new Error('no host');
      // Swap the whole host body (grid + loader + sentinel) with the filtered render,
      // carry over its pagination URL, then re-init infinite scroll on the new DOM.
      this.innerHTML = freshHost.innerHTML;
      this.dataset.nextUrl = freshHost.dataset.nextUrl || '';
      this._reinit();
      if (push) window.history.pushState({ plpFilter: url }, '', url);
    } catch (e) {
      // Any failure → fall back to a normal navigation so the user still gets there.
      if (push) window.location.assign(url);
    } finally {
      this.fetching = false;
      this._setLoading(false);
    }
  }

  // Re-bind grid/sentinel/loader references and restart reveal + infinite scroll
  // after the host body has been replaced by filterTo().
  _reinit() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    this.grid = this.querySelector('[data-grid]');
    this.sentinel = this.querySelector('[data-sentinel]');
    this.loader = this.querySelector('[data-loader]');
    this.items = this.grid ? Array.from(this.grid.children) : [];
    this.nextUrl = (this.dataset.nextUrl || '').trim();
    this.shown = 0;
    this.mode = null; // force apply() to re-enter mobile/desktop reveal
    this.apply();
  }
}

if (!customElements.get('spectrum-collection-plp')) {
  customElements.define('spectrum-collection-plp', SpectrumCollectionPlp);
}

// Intercept filter-bar link clicks so category / sub-collection filtering happens in
// place instead of loading a new page. Works for EVERY filter bar that pairs with the
// grid — tag-filter chips (.ctf) and sub-collection pills / bar / toggle (.ssp/.ssb/
// .ssto) — across all collection templates that use <spectrum-collection-plp>. Falls
// back to normal navigation if anything is missing (no grid, modified click, a
// non-collection link, or a fetch failure).
if (!window.__spectrumPlpFilterBound) {
  window.__spectrumPlpFilterBound = true;

  const plpHost = () => document.querySelector('spectrum-collection-plp');
  const BAR_SEL = '.ctf, .ssp, .ssb, .ssto'; // roots of the filter-bar sections

  const samePath = (a, b) => {
    try {
      return (
        new URL(a, window.location.origin).pathname ===
        new URL(b, window.location.origin).pathname
      );
    } catch (e) {
      return false;
    }
  };

  // Move the active state onto whichever link matches `url`, in every filter bar.
  const syncActive = (url) => {
    document.querySelectorAll(BAR_SEL).forEach((bar) => {
      bar.querySelectorAll('a[href]').forEach((a) => {
        a.classList.toggle('is-active', samePath(a.getAttribute('href'), url));
      });
    });
  };

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const link = e.target.closest('a[href]');
    if (!link || !link.closest(BAR_SEL)) return;
    const host = plpHost();
    if (!host || typeof host.filterTo !== 'function') return; // let it navigate
    let dest;
    try { dest = new URL(link.href, window.location.origin); } catch (er) { return; }
    // Only hijack same-origin collection links we can render in place.
    if (dest.origin !== window.location.origin || !/^\/collections\/[^/]/.test(dest.pathname)) return;
    e.preventDefault();
    // Neutralize the site's page-loader bar (frido-page-loader), whose capture-phase
    // click listener already showed the top bar + stashed a nav flag expecting a real
    // load. Same click event, before paint, so the bar never actually appears.
    const loaderEl = document.querySelector('[data-frido-toploader]');
    if (loaderEl) loaderEl.classList.remove('is-active');
    try { sessionStorage.removeItem('fridoNavLoading'); } catch (err) {}
    const to = dest.pathname + dest.search;
    syncActive(to);
    host.filterTo(to);
  });

  window.addEventListener('popstate', () => {
    const host = plpHost();
    if (!host || typeof host.filterTo !== 'function') return;
    const url = window.location.pathname + window.location.search;
    syncActive(url);
    host.filterTo(url, false);
  });
}


/* Immediate "updating" feedback for in-place category/tag filtering (filterTo). Injected
   once so it ships with the component on every collection template that uses it. */
(function () {
  if (typeof document === 'undefined' || document.getElementById('scplp-updating-styles')) return;
  var s = document.createElement('style');
  s.id = 'scplp-updating-styles';
  s.textContent =
    'spectrum-collection-plp [data-grid].scplp--updating{pointer-events:none;position:relative}' +
    /* A grey shimmer wash overlaid on the grid (a real appended element) — dims the cards
       to a loading/greyed state WITHOUT touching their opacity, which the reveal animation
       keeps overriding. Matches the sort effect greying the products while it reloads. */
    'spectrum-collection-plp .scplp-skel{position:absolute;inset:0;z-index:5;pointer-events:none;background:rgba(247,247,247,.62);overflow:hidden}' +
    'spectrum-collection-plp .scplp-skel::after{content:"";position:absolute;inset:0;' +
      'background:linear-gradient(100deg,transparent 35%,rgba(255,255,255,.75) 50%,transparent 65%);background-size:200% 100%;animation:scplp-shimmer 1.1s linear infinite}' +
    '@keyframes scplp-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}' +
    '@media (prefers-reduced-motion: reduce){spectrum-collection-plp .scplp-skel::after{animation:none}}';
  (document.head || document.documentElement).appendChild(s);
})();
